import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api.js';

/** Whole collections per page of the history (the full view of GET /customers/{id}/runs caps a page at 100). */
export const RUN_PAGE = 25;
/** The window the history reaches back: the 13 months onGuard keeps runs (the API takes at most 400 days). */
export const RUN_WINDOW_DAYS = 400;

/**
 * The collection history, whole rows, newest first, a page at a time (ENTRAID-347 Data collection, on D1's time
 * window and cursor). `loadMore` appends the next page while `hasMore`. `reloadKey` starts again from the newest.
 *
 * @returns {{ status: 'idle'|'loading'|'ready'|'error', runs: object[], error: string|null, hasMore: boolean,
 *   loadingMore: boolean, loadMore: () => void }}
 */
export function useRunPages(customerId, { enabled = true, reloadKey = 0 } = {}) {
  const [state, setState] = useState({ status: 'idle', runs: [], error: null, cursor: null, loadingMore: false });
  const req = useRef(0);

  useEffect(() => {
    if (!enabled || !customerId) return undefined;
    const mine = ++req.current;
    setState((prev) => ({ ...prev, status: 'loading', error: null, loadingMore: false }));
    Promise.resolve()
      .then(() => api.listRunHistory(customerId, { days: RUN_WINDOW_DAYS, view: 'full', limit: RUN_PAGE }))
      .then(
        (r) => { if (req.current === mine) setState({ status: 'ready', runs: Array.isArray(r?.runs) ? r.runs : [], error: null, cursor: r?.cursor ?? null, loadingMore: false }); },
        (e) => { if (req.current === mine) setState({ status: 'error', runs: [], error: e?.message || 'The collection history could not be loaded.', cursor: null, loadingMore: false }); },
      );
    return () => { req.current += 1; };
  }, [customerId, enabled, reloadKey]);

  const loadMore = useCallback(() => {
    const { cursor } = state;
    if (!cursor || state.loadingMore) return;
    const mine = req.current;
    setState((prev) => ({ ...prev, loadingMore: true }));
    Promise.resolve()
      .then(() => api.listRunHistory(customerId, { days: RUN_WINDOW_DAYS, view: 'full', limit: RUN_PAGE, cursor }))
      .then(
        (r) => { if (req.current === mine) setState((prev) => ({ ...prev, runs: [...prev.runs, ...(r?.runs ?? [])], cursor: r?.cursor ?? null, loadingMore: false })); },
        (e) => { if (req.current === mine) setState((prev) => ({ ...prev, loadingMore: false, error: e?.message || 'More collections could not be loaded.' })); },
      );
  }, [customerId, state]);

  return {
    status: enabled ? state.status : 'idle',
    runs: state.runs,
    error: state.error,
    hasMore: Boolean(state.cursor),
    loadingMore: state.loadingMore,
    loadMore,
  };
}
